// Offline-safe identity recovery.
//
// supabase-js's getSession() returns NULL as soon as the access token is within
// ~90s of expiry and a token refresh can't reach the network. So a field scout
// who's been offline ~1h loses their identity: the App auth gate bounces them to
// a Login screen they can't complete with no signal, and useActiveTenant /
// useCurrentUser (which key off getSession) lose the active tenant + role.
//
// But the session — including the JWT and its claims — is still sitting in
// localStorage under the supabase-js default key `sb-<project-ref>-auth-token`.
// These helpers read + decode that stored token REGARDLESS of expiry, so the
// auth gate + the tenant/user hooks can recover identity + claims offline and
// keep working (reads from the durable cache, writes via the offline queue).
// When the device is back online supabase-js refreshes the token normally; if
// the refresh token is itself revoked/expired the refresh fails online and the
// user is signed out then — so this only ever extends a still-valid session
// across an offline gap, never resurrects a revoked one.

import { supabase } from '@/lib/supabase'
import { wipePersistedQueryCacheForSignOut } from '@/lib/queryPersist'

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL as string

/** How long sign-out waits for the persisted-cache wipe before moving on. */
const CLEAR_CACHE_WAIT_MS = 1_500

// The two long-lived offline identity snapshot keys (the durable mirrors of the
// ['me'] and ['active-tenant'] queries). Declared here so the sign-out path can
// clear them without importing the hooks (which import THIS module) — see
// readLastMe/writeLastMe in useCurrentUser and readLastActiveTenant in
// useActiveTenant, which read these same keys.
export const LAST_ME_KEY     = 'kf49.lastMe'
export const LAST_TENANT_KEY = 'kf49.lastActiveTenant'
// Lives here rather than in usePermissions so clearOfflineIdentity() below can
// drop it without importing back into the hook (that direction is circular).
export const LAST_PERMS_KEY  = 'kf49.lastPermissions'
// supabase-js default storage key (no custom storageKey set in supabase.ts):
// sb-<project-ref>-auth-token, where ref is the URL host's first label.
const STORAGE_KEY = (() => {
  try { return `sb-${new URL(SUPA_URL).host.split('.')[0]}-auth-token` } catch { return '' }
})()

interface StoredSession {
  access_token?: string
  refresh_token?: string
  user?: { id?: string }
  expires_at?: number
}

export function readStoredSession(): StoredSession | null {
  try {
    const raw = STORAGE_KEY ? window.localStorage.getItem(STORAGE_KEY) : null
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as StoredSession) : null
  } catch { return null }
}

// True when a previously-signed-in session is on disk (token present). The auth
// gate uses this to keep an offline user in the app instead of bouncing them.
export function hasStoredSession(): boolean {
  const s = readStoredSession()
  return !!(s && typeof s.access_token === 'string' && s.access_token.length > 0)
}

// Remove the stored session WITHOUT the network. Only for a sign-out whose
// server call failed or hung (signOutThisDevice): supabase-js 2.108 returns the
// failed /logout as an error and leaves the session on disk, so with no
// connection "Sign out" would otherwise sign nobody out. ⚠️ The caller MUST
// reload hard afterwards — supabase-js still holds the session in memory and
// writes it back on its next refresh; a fresh boot reads storage and finds none.
export function dropStoredSession(): void {
  try { if (STORAGE_KEY) window.localStorage.removeItem(STORAGE_KEY) } catch { /* best effort */ }
}

export interface StoredClaims {
  sub:              string | null
  active_tenant_id: string | null
  tenant_status:    string | null
  // mig 710 claims — present once the auth hook emits them. Let the active
  // tenant KIND + the per-tenant ROLE survive offline straight from the stored
  // token (no current_tenant_kind/current_tenant_role RPC, no snapshot needed).
  tenant_kind:      string | null
  tenant_role:      string | null
  raw:              Record<string, unknown>
}

// Decode a JWT's payload even when it's expired. base64url-safe (a JWT segment
// uses -/_ and no padding, which plain atob() rejects) and try/catch-wrapped, so
// callers can use it for the LIVE token too instead of a raw atob() that throws.
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const seg = token.split('.')[1]
    if (!seg) return null
    const b64 = seg.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(seg.length / 4) * 4, '=')
    return JSON.parse(atob(b64)) as Record<string, unknown>
  } catch { return null }
}

export function decodeStoredClaims(): StoredClaims | null {
  const tok = readStoredSession()?.access_token
  if (!tok) return null
  const payload = decodeJwtPayload(tok)
  if (!payload) return null
  const rawTid = payload.active_tenant_id
  const tenantId = typeof rawTid === 'string' && rawTid !== 'undefined' && rawTid !== 'null' && rawTid.length > 0
    ? rawTid
    : null
  return {
    sub:              typeof payload.sub === 'string' ? payload.sub : null,
    active_tenant_id: tenantId,
    tenant_status:    typeof payload.tenant_status === 'string' ? payload.tenant_status : null,
    tenant_kind:      typeof payload.tenant_kind === 'string' ? payload.tenant_kind : null,
    tenant_role:      typeof payload.tenant_role === 'string' ? payload.tenant_role : null,
    raw:              payload,
  }
}

export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false
}

// Drop the offline identity snapshots. Called on sign-out so a fail-closed
// lockout can't be out-waited offline (offlineAuthed keys off the stored token,
// but the snapshots feed role/module access) and a stale snapshot never lingers
// for the next user on a shared device.
export function clearOfflineIdentity(): void {
  try {
    localStorage.removeItem(LAST_ME_KEY)
    localStorage.removeItem(LAST_TENANT_KEY)
    // The permission map is identity too, and it outlived sign-out — on a
    // shared tablet that left the previous user's map on disk for the next
    // one. usePermissions now also checks the user id, so this is defence in
    // depth rather than the only guard.
    localStorage.removeItem(LAST_PERMS_KEY)
  } catch { /* best effort */ }
}

// Sign out so the LOCAL session is ALWAYS removed, even offline.
//
// supabase-js's default signOut() uses scope:'global' — a network POST to
// /logout that THROWS on a network failure BEFORE it removes the local session.
// On a PWA that means an offline (or deactivated-while-offline) user whose
// fail-closed gate fires `signOut().catch(()=>{})` keeps a valid stored token on
// disk, and offlineAuthed (App.tsx) holds them in the app. We still attempt the
// global revoke first (so an online sign-out revokes the refresh token on every
// device as before); if that throws we fall back to scope:'local', which skips
// the network call and is guaranteed to remove the local session + emit
// SIGNED_OUT. Either way we then clear the identity snapshots.
export async function signOutEverywhere(): Promise<void> {
  try {
    await supabase.auth.signOut()
  } catch {
    try { await supabase.auth.signOut({ scope: 'local' }) } catch { /* token will be cleared below */ }
  }
  clearOfflineIdentity()
  // The persisted cache now carries face templates; a shared phone must not
  // keep them after the person signs out. Bounded: IndexedDB is known to wedge
  // on the fleet's browsers (see queryPersist's restore timeout), and a hung
  // delete must never hold the Sign out button.
  await Promise.race([
    wipePersistedQueryCacheForSignOut(),
    new Promise<void>(resolve => setTimeout(resolve, CLEAR_CACHE_WAIT_MS)),
  ])
}
